// @flow

import React, { type Node } from 'react';
import styled, { css } from 'styled-components';
import { useSubmitting } from '@tectonics/prs/submitting';
import spinnerImageSrc from './spinner.svg';
import { Button } from '../../../../../commons/atoms';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';

export function PlaceOrderButton(): Node {
  const submitting = useSubmitting();
  return (
    <Root
      flat
      type="submit"
      disabled={submitting}
      data-test-id="submit"
    >
      <SpinnerImage src={spinnerImageSrc} submitting={submitting} alt="loading indicator" />
      <ButtonMessageWrapper>
        <ButtonMessage/>
      </ButtonMessageWrapper>
    </Root>
  );
}

function ButtonMessage(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  switch(country){
    case 'br':
      return <BrazilianButtonMessage />;
    case 'ar':
      return <ArgentineButtonMessage />;
    case 'bo':
      return <BolivianButtonMessage />;
    case 'cl':
      return <ChileanButtonMessage />;
    case 'co':
      return <ColombianButtonMessage />;
    case 'ec':
      return <EcuadorianButtonMessage />;
    case 'mx':
      return <MexicanButtonMessage />;
    case 'pe':
      return <PeruvianButtonMessage />;
    case 'py':
      return <ParaguayanButtonMessage />;
    case 'uy':
      return <UruguayanButtonMessage />;
    case 'za':
      return <SouthAfricanButtonMessage />;
    case 'ke':
      return <KenyanButtonMessage />;
    case 'ng':
      return <NigerianButtonMessage />;
    default:
      throw new Error(`PlaceOrderButton message for country: ${country} not found`);
  }
}

function ArgentineButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
    case 'debitcard':
      return <p>Pagar</p>;

    case 'cash':
      return <p>Pagar con cupon</p>;

    case 'ewallet':
      return 'Pagar con MercadoPago';

    case '':
      return <p>Seleccione un método de pago</p>;

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function BolivianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'cash':
      return <p>Generar número de recaudación</p>;

    case '':
      return <p>Seleccione un método de pago</p>;

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ChileanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'multicaja':
      return <p>Pagar con Klap (Multicaja)</p>;

    case 'webpay':
      return <p>Pagar con WebPay</p>;

    case 'creditcard':
      return <p>Pagar con Tarjeta de Crédito</p>;

    case 'debitcard':
      return <p>Pagar con Tarjeta de Débito</p>;

    case 'sencillito':
      return <p>Pagar con Sencillilto</p>;

    case 'servipag':
      return <p>Pagar con ServiPag</p>;

    case '':
      return 'Seleccione un método de pago';
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function BrazilianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return 'Pagar com cartão';

    case 'boleto':
      return 'Gerar boleto';

    case 'banktransfer':
      return 'Gerar transferência';

    case 'pix':
      return 'Pagar com Pix';

    case '':
      return 'Selecione um método de pagamento';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function MexicanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
    case 'debitcard':
      return 'Pagar con Tarjeta';

    case 'paynet':
      return 'Pagar con Cupon';

    case 'spei':
      return 'Pagar con SPEI';

    case 'cash':
    case 'oxxo':
    case 'oxxopay':
      return 'Generar boleta de pago';

    case 'ewallet':
      return 'Pagar con MercadoPago';

    case '':
      return 'Seleccione un método de pago';

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ColombianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'eft':
      return <p>Pagar con PAGOS EN LÍNEA</p>;

    case 'cash':
      return <p>Pagar con Efecty</p>;

    case 'creditcard':
      return 'Pagar';

    case 'ewallet':
      return <p>Pagar con NEQUI</p>;

    case 'externalpayprovider':
      return <p>Pagar con PSE AVANZA</p>;

    case '':
      return <p>Seleccione un método de pago</p>;

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function EcuadorianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'safetypay-cash':
      return <p>Generar código de pago en efectivo</p>;

    case 'safetypay-online':
      return <p>Pagar vía transferência</p>;

    case '':
      return <p>Seleccione un método de pago</p>;

    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function PeruvianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return <p>Pagar</p>;
    case '':
      return <p>Seleccione un método de pago</p>;
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function ParaguayanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return <p>Pagar</p>;
    case '':
      return <p>Seleccione un método de pago</p>;
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function UruguayanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
    case 'debitcard':
      return <p>Pagar</p>;
    case '':
      return <p>Seleccione un método de pago</p>;
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function SouthAfricanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'creditcard':
      return <p>Pay with credit card</p>;
    case '':
      return <p>Select a payment method</p>;
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function KenyanButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'ewallet':
      return <p>Pay with M-Pesa</p>;
    case '':
      return <p>Select a payment method</p>;
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

function NigerianButtonMessage(): Node {
  const [paymentMethod] = useUserInputValue('selectedPaymentMethod');

  switch (paymentMethod) {
    case 'ewallet':
      return <p>Pay with USSD</p>;
    case '':
      return <p>Select a payment method</p>;
    default:
      throw new Error(`PlaceOrderButton message for method: ${paymentMethod} not found`);
  }
}

const Root = styled(Button)`
  background: rgb(35, 151, 212);
  font-size: 1.3rem;
  font-weight: 300;
  color: white;
  height: 60px;
  padding: 0 15px;
  border: none;
`;

const SpinnerImage = styled.img`
  height: 100%;
  display: none;
  ${props => props.submitting && css`
    display: block;
    margin: auto;
  `}
`;

const ButtonMessageWrapper = styled.span`
  height: 100%;
  ${props => props.submitting && css`
    display: none;
  `}
`;
